import { create } from 'zustand';
import { DatasetType, MarkerStore } from '../types/marker';
import { persist } from 'zustand/middleware';
import { createDebouncedJSONStorage } from 'zustand-debounce';
import indexedDbStorage from '../utils/indexeddb-zustand-store';

export const storageName = 'MOOD_marker_store';

export const useMarkerStore = create<MarkerStore>()(
  persist(
    (set, get) => ({
      markers: [],
      activeMarkerId: null,
      activeMarker: () => get().markers.find((marker) => marker.active) || null,
      isAddMarkerMode: false,

      addMarker: (marker) =>
        set((state) => ({
          markers: [
            ...state.markers.map((m) => ({ ...m, active: false })),
            marker,
          ],
          activeMarkerId: marker.id,
        })),
      deleteMarker: (markerId) =>
        set((state) => ({
          markers: state.markers.filter((marker) => marker.id !== markerId),
          activeMarkerId: null,
        })),

      updateMarker: (markerId, updates) => {
        return set((state) => ({
          markers: state.markers.map((m) =>
            m.id === markerId ? { ...m, ...updates } : m
          ),
        }));
      },

      setActiveMarker: (markerId) =>
        set((state) => {
          const updatedMarkers = state.markers.map((marker) => ({
            ...marker,
            active: marker.id === markerId,
          }));

          return {
            markers: updatedMarkers,
            activeMarkerId: markerId,
          };
        }),
      setAddMarkerMode: (status) => set({ isAddMarkerMode: status }),
      setActiveKey: (markerId, metadataType) =>
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId ? { ...marker, key: metadataType } : marker
          ),
        })),

      setResetMarker: () =>
        set(() => ({
          markers: [],
          activeMarkerId: null,
          isAddMarkerMode: false,
        })),

      setDownloadPage: (markerId, panelName, value) =>
        set((state) => {
          const updatedMarkers = state.markers.map((marker) => {
            if (marker.id !== markerId) return marker;

            const currentDownloadPages = Array.isArray(marker.downloadPage)
              ? [...marker.downloadPage]
              : [];

            const existingIndex = currentDownloadPages.findIndex(
              (item) => item && item.radioKey === panelName
            );

            let newDownloadPages;

            if (existingIndex >= 0) {
              newDownloadPages = [...currentDownloadPages];
              newDownloadPages[existingIndex] = value;
            } else {
              newDownloadPages = [...currentDownloadPages, value];
            }

            return {
              ...marker,
              downloadPage: newDownloadPages,
            };
          });

          return {
            markers: updatedMarkers,
          };
        }),

      setWaterDepth: (
        markerId: string,
        panelName: string,
        value: string | number | null
      ) => {
        set((state) => {
          const markerToUpdate = state.markers.find(
            (marker) => marker.id === markerId
          );
          if (!markerToUpdate) return state;

          const currentValues = markerToUpdate.waterDepth || {};
          const newValue = value === null ? '-' : value;

          const updatedDepthData = {
            ...currentValues,
            [panelName]: newValue,
          };

          return {
            markers: state.markers.map((marker) =>
              marker.id === markerId
                ? { ...marker, waterDepth: updatedDepthData }
                : marker
            ),
          };
        });
      },

      setCellSize: (
        markerId: string,
        panelName: string,
        value: string | number | null
      ) => {
        set((state) => {
          const markerToUpdate = state.markers.find(
            (marker) => marker.id === markerId
          );
          if (!markerToUpdate) return state;

          const currentValues = markerToUpdate.cellSize || {};
          const newValue = value === null ? '-' : value;

          const updatedCellSizeData = {
            ...currentValues,
            [panelName]: newValue,
          };

          return {
            markers: state.markers.map((marker) =>
              marker.id === markerId
                ? { ...marker, cellSize: updatedCellSizeData }
                : marker
            ),
          };
        });
      },

      setDatasetOffline: (
        markerId: string,
        panelName: string,
        value: boolean
      ) => {
        set((state) => {
          const markerToUpdate = state.markers.find(
            (marker) => marker.id === markerId
          );
          if (!markerToUpdate) return state;

          const currentValues = markerToUpdate.isDatasetOffline || {};

          const updatedOfflineData = {
            ...currentValues,
            [panelName]: value,
          };

          return {
            markers: state.markers.map((marker) =>
              marker.id === markerId
                ? { ...marker, isDatasetOffline: updatedOfflineData }
                : marker
            ),
          };
        });
      },

      setDatasetReport: (
        markerId: string,
        panelName: string,
        value: boolean
      ) => {
        set((state) => {
          const markerToUpdate = state.markers.find(
            (marker) => marker.id === markerId
          );
          if (!markerToUpdate) return state;

          const currentValues = markerToUpdate.isDatasetHasReport || {};

          const updatedReportData = {
            ...currentValues,
            [panelName]: value,
          };

          return {
            markers: state.markers.map((marker) =>
              marker.id === markerId
                ? { ...marker, isDatasetHasReport: updatedReportData }
                : marker
            ),
          };
        });
      },

      setActiveDatasetKey: (
        markerId: string,
        datasetKey: DatasetType,
        newActiveDataset: string
      ) => {
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId
              ? {
                  ...marker,
                  activeDatasetKey: datasetKey,
                  dataset: newActiveDataset,
                }
              : marker
          ),
        }));
      },

      setActivePanel: (markerId: string, datasetKey: DatasetType | string) => {
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId
              ? { ...marker, activePanel: datasetKey }
              : marker
          ),
        }));
      },
    }),
    {
      name: storageName,
      storage: createDebouncedJSONStorage(indexedDbStorage, {
        debounceTime: 500,
      }),
      version: 2,
      partialize: (state) => ({
        markers: state.markers,
        activeMarkerId: state.activeMarkerId,
        isAddMarkerMode: state.isAddMarkerMode,
      }),
    }
  )
);

export const useActiveMarker = () => {
  return useMarkerStore((state) => {
    const id = state.activeMarkerId;
    return id ? state.markers.find((m) => m.id === id) || null : null;
  });
};

export const isStoreEmpty = (state: MarkerStore) => {
  return (
    state.markers.length === 0 &&
    state.activeMarkerId === null /*&& !state.isAddMarkerMode*/
  );
};
